import React, { useState, useEffect } from 'react';
import { Download, Search, Eye, Phone, Mail, Calendar, User, Send, X, Trash2, MessageSquare, Copy, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { apiClient } from '../../lib/api-client';
import { toast } from 'sonner';
import { ClientPaginationWrapper } from '../ui/ClientPaginationWrapper';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface UserWithContacts {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  contactsUploadedAt: string;
  contactsCsvUrl: string;
  contactsCount: number;
  location?: {
    latitude: number;
    longitude: number;
    updatedAt: string;
    device: string;
    platform: string;
  };
  distance?: number;
}

interface ContactEmail {
  email: string;
  label: string;
}

interface ContactPhoneNumber {
  number: string;
  label: string;
}

interface Contact {
  name: string;
  // New structure
  phones?: string[];
  emails?: string | string[];
  // Old structure (for backward compatibility)
  phoneNumbers?: ContactPhoneNumber[];
  emailsOld?: ContactEmail[];
}

export function AdminUserContacts() {
  const [users, setUsers] = useState<UserWithContacts[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [radius, setRadius] = useState('');
  const [viewContactsModal, setViewContactsModal] = useState(false);
  const [viewingContacts, setViewingContacts] = useState<Contact[]>([]);
  const [viewingUser, setViewingUser] = useState<UserWithContacts | null>(null);
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [selectedUserForEmail, setSelectedUserForEmail] = useState<UserWithContacts | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [emailFilter, setEmailFilter] = useState('');
  const [allEmails, setAllEmails] = useState<string[]>([]);
  const [removedEmails, setRemovedEmails] = useState<Set<string>>(new Set());
  const [manualEmail, setManualEmail] = useState('');
  const [smtpAccounts, setSMTPAccounts] = useState<any[]>([]);
  const [selectedSMTPAccount, setSelectedSMTPAccount] = useState<string>('');
  const [loadingSMTPAccounts, setLoadingSMTPAccounts] = useState(false);
  
  // SMS Modal states
  const [sendSmsModal, setSendSmsModal] = useState(false);
  const [selectedUserForSms, setSelectedUserForSms] = useState<UserWithContacts | null>(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [phoneList, setPhoneList] = useState<string[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [allPhones, setAllPhones] = useState<string[]>([]);
  const [removedPhones, setRemovedPhones] = useState<Set<string>>(new Set());
  const [manualPhone, setManualPhone] = useState('');
  const [contactsPage, setContactsPage] = useState(1);
  const contactsPerPage = 20;
  
  const limit = 20;

  useEffect(() => {
    fetchUsersWithContacts();
  }, [currentPage, search, latitude, longitude, radius]);

  const fetchUsersWithContacts = async () => {
    try {
      setLoading(true);
      let url = `/contacts/users?page=${currentPage}&limit=${limit}&search=${search}`;
      
      // Add location filters if all three are provided
      if (latitude && longitude && radius) {
        url += `&latitude=${latitude}&longitude=${longitude}&radius=${radius}`;
      }
      
      const response = await apiClient.get(url);
      
      if (response.data.success) {
        setUsers(response.data.data.users);
        setTotalPages(response.data.data.pagination.pages);
        setTotalUsers(response.data.data.pagination.total);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch users with contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (userId: string, userEmail: string) => {
    try {
      const response = await apiClient.get(`/contacts/download/${userId}`);
      
      if (response.data.success) {
        // Open the CSV URL in a new tab to download
        window.open(response.data.data.csvUrl, '_blank');
        toast.success('Download started');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download contacts');
    }
  };

  const handleDeleteContacts = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete all contacts for ${userName}? This will allow them to upload contacts again.`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/contacts/delete/${userId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh the list
        fetchUsersWithContacts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete contacts');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    // Note: setCurrentPage(1) triggers the useEffect which calls fetchUsersWithContacts()
    // No direct call needed here to avoid double API call
  };

  const handleViewContacts = async (userId: string) => {
    try {
      const response = await apiClient.get(`/contacts/view/${userId}`);
      
      if (response.data.success) {
        setViewingContacts(response.data.data.contacts);
        setViewingUser(users.find(user => user._id === userId) || null);
        setContactsPage(1); // Reset to first page when opening modal
        setViewContactsModal(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to view contacts');
    }
  };

  const handleOpenSendEmail = async (user: UserWithContacts) => {
    setSelectedUserForEmail(user);
    setLoadingEmails(true);
    setSendEmailModal(true);
    setRemovedEmails(new Set());
    setEmailFilter('');
    setLoadingSMTPAccounts(true);
    
    try {
      // Load emails
      const response = await apiClient.get(`/contacts/view/${user._id}`);
      
      if (response.data.success) {
        const emails = new Set<string>();
        response.data.data.contacts.forEach((contact: Contact) => {
          // New structure - direct string array
          if (contact.emails && Array.isArray(contact.emails)) {
            contact.emails.forEach((email: string) => {
              if (email && !email.includes('*') && !email.includes('#')) {
                emails.add(email);
              }
            });
          }
          // Old structure - array of objects
          else if (contact.emailsOld && Array.isArray(contact.emailsOld)) {
            contact.emailsOld.forEach((emailObj: ContactEmail) => {
              if (emailObj.email && !emailObj.email.includes('*') && !emailObj.email.includes('#')) {
                emails.add(emailObj.email);
              }
            });
          }
        });
        const emailArray = Array.from(emails);
        setAllEmails(emailArray);
        setEmailList(emailArray);
      }
      
      // Load SMTP accounts
      const smtpResponse = await apiClient.get('/admin/smtp');
      if (smtpResponse.data.success) {
        setSMTPAccounts(smtpResponse.data.data.accounts);
        // Set first active account as default
        const activeAccount = smtpResponse.data.data.accounts.find((acc: any) => acc.isActive);
        if (activeAccount) {
          setSelectedSMTPAccount(activeAccount._id);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoadingEmails(false);
      setLoadingSMTPAccounts(false);
    }
  };

  const handleToggleEmail = (email: string) => {
    if (emailList.includes(email)) {
      setRemovedEmails(prev => new Set([...prev, email]));
      setEmailList(emailList.filter(e => e !== email));
    } else {
      setRemovedEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
      setEmailList([...emailList, email]);
    }
  };

  const handleFilterEmails = (filter: string) => {
    setEmailFilter(filter);
    if (filter) {
      const filtered = allEmails.filter(email => 
        email.toLowerCase().includes(filter.toLowerCase()) && !removedEmails.has(email)
      );
      // Keep manually added emails that match filter
      const manuallyAdded = emailList.filter(email => !allEmails.includes(email) && email.toLowerCase().includes(filter.toLowerCase()));
      setEmailList([...filtered, ...manuallyAdded]);
    } else {
      const notRemoved = allEmails.filter(email => !removedEmails.has(email));
      const manuallyAdded = emailList.filter(email => !allEmails.includes(email));
      setEmailList([...notRemoved, ...manuallyAdded]);
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setRemovedEmails(prev => new Set([...prev, emailToRemove]));
    setEmailList(emailList.filter(email => email !== emailToRemove));
  };

  const handleAddManualEmail = () => {
    const trimmedEmail = manualEmail.trim();
    if (trimmedEmail && !emailList.includes(trimmedEmail) && !trimmedEmail.includes('*') && !trimmedEmail.includes('#')) {
      setEmailList(prev => [...prev, trimmedEmail]);
      setManualEmail('');
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserForEmail || emailList.length === 0) return;
    
    if (!emailSubject || !emailBody) {
      toast.error('Please fill in subject and body');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await apiClient.post(`/contacts/send-email/${selectedUserForEmail._id}`, {
        subject: emailSubject,
        body: emailBody,
        emails: emailList,
        smtpAccountId: selectedSMTPAccount
      });
      
      if (response.data.success) {
        toast.success(`Email sent to ${response.data.data.successCount} recipients`);
        setSendEmailModal(false);
        setEmailSubject('');
        setEmailBody('');
        setEmailList([]);
        setRemovedEmails(new Set());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenSendSms = async (user: UserWithContacts) => {
    setSelectedUserForSms(user);
    setLoadingPhones(true);
    setSendSmsModal(true);
    setRemovedPhones(new Set());
    setPhoneFilter('');
    
    try {
      const response = await apiClient.get(`/contacts/view/${user._id}`);
      
      if (response.data.success) {
        const phones = new Set<string>();
        response.data.data.contacts.forEach((contact: Contact) => {
          // New structure - direct string array
          if (contact.phones && Array.isArray(contact.phones)) {
            contact.phones.forEach((phone: string) => {
              if (phone && !phone.includes('*') && !phone.includes('#')) {
                phones.add(phone);
              }
            });
          }
          // Old structure - array of objects
          else if (contact.phoneNumbers && Array.isArray(contact.phoneNumbers)) {
            contact.phoneNumbers.forEach((phoneObj: ContactPhoneNumber) => {
              if (phoneObj.number && !phoneObj.number.includes('*') && !phoneObj.number.includes('#')) {
                phones.add(phoneObj.number);
              }
            });
          }
        });
        const phoneArray = Array.from(phones);
        setAllPhones(phoneArray);
        setPhoneList(phoneArray);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load phone numbers');
    } finally {
      setLoadingPhones(false);
    }
  };

  const handleTogglePhone = (phone: string) => {
    if (phoneList.includes(phone)) {
      setRemovedPhones(prev => new Set([...prev, phone]));
      setPhoneList(phoneList.filter(p => p !== phone));
    } else {
      setRemovedPhones(prev => {
        const newSet = new Set(prev);
        newSet.delete(phone);
        return newSet;
      });
      setPhoneList([...phoneList, phone]);
    }
  };

  const handleFilterPhones = (filter: string) => {
    setPhoneFilter(filter);
    if (filter) {
      const filtered = allPhones.filter(phone => 
        phone.toLowerCase().includes(filter.toLowerCase()) && !removedPhones.has(phone)
      );
      // Keep manually added phones that match filter
      const manuallyAdded = phoneList.filter(phone => !allPhones.includes(phone) && phone.toLowerCase().includes(filter.toLowerCase()));
      setPhoneList([...filtered, ...manuallyAdded]);
    } else {
      const notRemoved = allPhones.filter(phone => !removedPhones.has(phone));
      const manuallyAdded = phoneList.filter(phone => !allPhones.includes(phone));
      setPhoneList([...notRemoved, ...manuallyAdded]);
    }
  };

  const handleRemovePhone = (phoneToRemove: string) => {
    setRemovedPhones(prev => new Set([...prev, phoneToRemove]));
    setPhoneList(phoneList.filter(phone => phone !== phoneToRemove));
  };

  const handleAddManualPhone = () => {
    const trimmedPhone = manualPhone.trim();
    if (trimmedPhone && !phoneList.includes(trimmedPhone) && !trimmedPhone.includes('*') && !trimmedPhone.includes('#')) {
      setPhoneList(prev => [...prev, trimmedPhone]);
      setManualPhone('');
    }
  };

  const handleSendSms = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserForSms || phoneList.length === 0) return;
    
    if (!smsMessage) {
      toast.error('Please fill in the message');
      return;
    }
    
    setSendingSms(true);
    try {
      const response = await apiClient.post(`/contacts/send-sms/${selectedUserForSms._id}`, {
        message: smsMessage,
        phones: phoneList
      });
      
      if (response.data.success) {
        toast.success(`SMS sent to ${response.data.data.successCount} recipients`);
        setSendSmsModal(false);
        setSmsMessage('');
        setPhoneList([]);
        setRemovedPhones(new Set());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500">
            <Phone className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">User Contacts</h2>
            <p className="text-gray-600 text-sm mt-1">
              View and download contacts uploaded by users ({totalUsers} users)
            </p>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by email, name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit">
              Search
            </Button>
          </div>
          
          {/* Location Filter */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="inline h-4 w-4 mr-1" />
                Latitude
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., 28.6139"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., 77.2090"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Radius (km)
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., 8"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      setLatitude(position.coords.latitude.toString());
                      setLongitude(position.coords.longitude.toString());
                      toast.success('Location obtained successfully');
                    },
                    (error) => {
                      toast.error('Failed to get location: ' + error.message);
                    }
                  );
                } else {
                  toast.error('Geolocation is not supported by your browser');
                }
              }}
            >
              <MapPin className="h-4 w-4 mr-1" />
              Get My Location
            </Button>
            {(latitude || longitude || radius) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLatitude('');
                  setLongitude('');
                  setRadius('');
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading...
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No users have uploaded contacts yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contacts
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{user.email}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">
                              {new Date(user.contactsUploadedAt).toLocaleDateString('en-IN', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {user.location && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="font-mono text-[10px]">
                                {user.location.latitude.toFixed(2)}, {user.location.longitude.toFixed(2)}
                              </span>
                              {user.distance !== undefined && (
                                <span className="text-green-600 font-medium ml-1">
                                  ({user.distance} km)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Phone className="h-3 w-3" />
                        <span className="text-xs">{user.phone}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.contactsCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(user._id, user.email)}
                          className="gap-1 h-7 px-2 text-xs"
                        >
                          <Download className="h-3 w-3" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewContacts(user._id)}
                          className="gap-1 h-7 px-2 text-xs"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenSendEmail(user)}
                          className="gap-1 h-7 px-2 text-xs"
                        >
                          <Send className="h-3 w-3" />
                          Email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenSendSms(user)}
                          className="gap-1 h-7 px-2 text-xs"
                        >
                          <MessageSquare className="h-3 w-3" />
                          SMS
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteContacts(user._id, `${user.firstName} ${user.lastName}`)}
                          className="gap-1 h-7 px-2 text-xs"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalUsers > 0 && (
          <div className="px-6 py-4 border-t">
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalUsers}
              itemsPerPage={limit}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* View Contacts Modal */}
      {viewContactsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold">Contacts for {viewingUser?.firstName} {viewingUser?.lastName}</h2>
                  <p className="text-sm text-gray-500">{viewingContacts.length} contacts</p>
                </div>
              </div>
              <button
                onClick={() => setViewContactsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <ClientPaginationWrapper
                items={viewingContacts}
                itemsPerPage={contactsPerPage}
                currentPage={contactsPage}
                onPageChange={setContactsPage}
                renderItems={(paginatedContacts) => (
                  <div className="space-y-3">
                    {paginatedContacts.map((item, index) => {
                      const contact = item as Contact;
                      return (
                  <div 
                    key={`${contact.name}-${index}`} 
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* Contact Initial */}
                      <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Contact Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 mb-2">{contact.name}</h3>
                        
                        {/* Emails - New Structure */}
                        {contact.emails && Array.isArray(contact.emails) && contact.emails.length > 0 && (
                          <div className="space-y-1 mb-2">
                            {contact.emails.map((email: string, emailIndex) => (
                              <div key={emailIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                <Mail className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{email}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Emails - Old Structure (fallback) */}
                        {contact.emailsOld && Array.isArray(contact.emailsOld) && contact.emailsOld.length > 0 && (
                          <div className="space-y-1 mb-2">
                            {contact.emailsOld.map((email, emailIndex) => (
                              <div key={emailIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                <Mail className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{email.email}</span>
                                <span className="text-xs text-gray-400">({email.label})</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Phone Numbers - New Structure */}
                        {contact.phones && Array.isArray(contact.phones) && contact.phones.length > 0 && (
                          <div className="space-y-1">
                            {contact.phones.map((phone: string, phoneIndex) => (
                              <div key={phoneIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                <Phone className="h-4 w-4 flex-shrink-0" />
                                <span>{phone}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Phone Numbers - Old Structure (fallback) */}
                        {contact.phoneNumbers && Array.isArray(contact.phoneNumbers) && contact.phoneNumbers.length > 0 && (
                          <div className="space-y-1">
                            {contact.phoneNumbers.map((phone, phoneIndex) => (
                              <div key={phoneIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                <Phone className="h-4 w-4 flex-shrink-0" />
                                <span>{phone.number}</span>
                                <span className="text-xs text-gray-400">({phone.label})</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ); })}

              </div>
            )}
          />
        </div>

            {/* Modal Footer */}
            <div className="p-6 border-t bg-gray-50">
              <Button onClick={() => setViewContactsModal(false)} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {sendEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-blue-500">
                  <Send className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold">Send Email to {selectedUserForEmail?.firstName} {selectedUserForEmail?.lastName}</h2>
                  <p className="text-sm text-gray-500">{emailList.length} recipients</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSendEmailModal(false);
                  setEmailSubject('');
                  setEmailBody('');
                  setEmailList([]);
                  setRemovedEmails(new Set());
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSendEmail} className="space-y-6">
                {/* Email List */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block font-medium text-gray-700">
                      Recipients ({emailList.length})
                    </label>
                    {!loadingEmails && allEmails.length > 0 && (
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={emailFilter}
                          onChange={(e) => {
                            const filter = e.target.value.toLowerCase();
                            setEmailFilter(filter);
                            if (filter) {
                              setEmailList(allEmails.filter(email => email.toLowerCase().includes(filter)));
                            } else {
                              setEmailList(allEmails);
                            }
                          }}
                          placeholder="Filter emails (e.g., gmail.com)"
                          className="w-64 text-sm"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEmailFilter('');
                            setEmailList(allEmails);
                          }}
                        >
                          Reset
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Quick Filter Buttons */}
                  {!loadingEmails && allEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-gray-500">Quick Filters:</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          const gmailOnly = allEmails.filter(email => 
                            email.toLowerCase().includes('@gmail.com') && !removedEmails.has(email)
                          );
                          setEmailList(gmailOnly);
                          setEmailFilter('@gmail.com');
                        }}
                      >
                        Gmail Only
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          const yahooOnly = allEmails.filter(email => 
                            email.toLowerCase().includes('@yahoo.com') && !removedEmails.has(email)
                          );
                          setEmailList(yahooOnly);
                          setEmailFilter('@yahoo.com');
                        }}
                      >
                        Yahoo Only
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          const outlookOnly = allEmails.filter(email => 
                            (email.toLowerCase().includes('@outlook.com') || email.toLowerCase().includes('@hotmail.com')) && !removedEmails.has(email)
                          );
                          setEmailList(outlookOnly);
                          setEmailFilter('outlook/hotmail');
                        }}
                      >
                        Outlook Only
                      </Button>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(emailList.join(', '));
                            toast.success(`Copied ${emailList.length} emails to clipboard`);
                          } catch (error) {
                            toast.error('Failed to copy to clipboard');
                          }
                        }}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy All ({emailList.length})
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="text-xs h-7"
                        onClick={() => {
                          setEmailList([]);
                          setRemovedEmails(new Set(allEmails));
                        }}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Remove All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          setEmailList(allEmails);
                          setRemovedEmails(new Set());
                          setEmailFilter('');
                        }}
                      >
                        Select All
                      </Button>
                    </div>
                  )}
                  
                  {loadingEmails ? (
                    <div className="flex items-center justify-center p-8 border-2 border-dashed rounded-lg">
                      <div className="text-center">
                        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Loading emails...</p>
                      </div>
                    </div>
                  ) : emailList.length === 0 ? (
                    <div className="p-8 border-2 border-dashed rounded-lg text-center">
                      <Mail className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-500 mb-4">No emails in list</p>
                      {/* Add Email Form */}
                      <div className="flex items-center justify-center gap-2 max-w-md mx-auto">
                        <Input
                          type="email"
                          value={manualEmail}
                          onChange={(e) => setManualEmail(e.target.value)}
                          placeholder="Enter email address"
                          className="flex-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddManualEmail();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          onClick={handleAddManualEmail}
                          disabled={!manualEmail.trim()}
                          className="gap-2"
                        >
                          <Send className="h-4 w-4" />
                          Add Email
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
                      <div className="flex flex-wrap gap-2">
                        {emailList.map(email => (
                          <div 
                            key={email} 
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border shadow-sm text-sm"
                          >
                            <Mail className="h-3.5 w-3.5 text-blue-500" />
                            <span className="font-medium">{email}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail(email)}
                              className="p-0.5 hover:bg-red-100 rounded-full transition-colors"
                            >
                              <X className="h-3.5 w-3.5 text-red-500" />
                            </button>
                          </div>
                        ))}
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border shadow-sm text-sm">
                          <Mail className="h-3.5 w-3.5 text-blue-500" />
                          <input
                            type="text"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="Add email"
                            className="w-32 text-sm outline-none"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddManualEmail();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleAddManualEmail}
                            className="p-0.5 hover:bg-green-100 rounded-full transition-colors"
                          >
                            <Send className="h-3.5 w-3.5 text-green-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* SMTP Account Selector */}
                <div className="space-y-2">
                  <label className="block font-medium text-gray-700">
                    Send From (SMTP Account) *
                  </label>
                  {loadingSMTPAccounts ? (
                    <div className="flex items-center justify-center p-4 border rounded-lg">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading SMTP accounts...</span>
                    </div>
                  ) : smtpAccounts.length === 0 ? (
                    <div className="p-4 border rounded-lg bg-yellow-50 text-yellow-800 text-sm">
                      ⚠️ No SMTP accounts available. Please add an SMTP account first.
                    </div>
                  ) : (
                    <select
                      value={selectedSMTPAccount}
                      onChange={(e) => setSelectedSMTPAccount(e.target.value)}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select SMTP Account</option>
                      {smtpAccounts.map((account: any) => (
                        <option key={account._id} value={account._id}>
                          {account.fromName} ({account.email}) - Daily: {account.todayUsage || 0}/{account.dailyLimit}
                          {!account.isActive && ' - INACTIVE'}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedSMTPAccount && smtpAccounts.length > 0 && (
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span>ℹ️</span>
                      <span>
                        {(() => {
                          const account = smtpAccounts.find((acc: any) => acc._id === selectedSMTPAccount);
                          if (!account) return '';
                          return `Emails will be sent from: ${account.fromEmail || account.email} (${account.smtpHost})`;
                        })()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <label className="block font-medium text-gray-700">
                    Subject *
                  </label>
                  <Input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    className="w-full"
                    required
                  />
                </div>

                {/* Body (HTML Support) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block font-medium text-gray-700">
                      Email Body (HTML supported) *
                    </label>
                    <span className="text-xs text-gray-500">You can use HTML tags</span>
                  </div>
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Enter email body... HTML is supported (e.g., <h1>Hello</h1>, <p>Welcome to our service!</p>, <a href='...'>Click here</a>)"
                    className="w-full h-64 p-3 border rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>📝 HTML Tips:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li>Use &lt;h1&gt;...&lt;/h1&gt; for headings</li>
                      <li>Use &lt;p&gt;...&lt;/p&gt; for paragraphs</li>
                      <li>Use &lt;a href="url"&gt;Link&lt;/a&gt; for links</li>
                      <li>Use &lt;strong&gt;...&lt;/strong&gt; for bold text</li>
                      <li>Use &lt;br&gt; for line breaks</li>
                    </ul>
                  </div>
                </div>

                {/* Preview (Optional) */}
                {emailBody && (
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">
                      Preview
                    </label>
                    <div 
                      className="border rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: emailBody }}
                    />
                  </div>
                )}
              </form>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSendEmailModal(false);
                  setEmailSubject('');
                  setEmailBody('');
                  setEmailList([]);
                  setRemovedEmails(new Set());
                }}
                className="flex-1"
                disabled={sendingEmail}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSendEmail}
                disabled={sendingEmail || emailList.length === 0 || !emailSubject || !emailBody}
                className="flex-1 gap-2"
              >
                {sendingEmail ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send to {emailList.length} recipient{emailList.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send SMS Modal */}
      {sendSmsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-blue-500">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold">Send SMS to {selectedUserForSms?.firstName} {selectedUserForSms?.lastName}</h2>
                  <p className="text-sm text-gray-500">{phoneList.length} recipients</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSendSmsModal(false);
                  setSmsMessage('');
                  setPhoneList([]);
                  setRemovedPhones(new Set());
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSendSms} className="space-y-6">
                {/* Phone List */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block font-medium text-gray-700">
                      Recipients ({phoneList.length})
                    </label>
                    {!loadingPhones && allPhones.length > 0 && (
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={phoneFilter}
                          onChange={(e) => handleFilterPhones(e.target.value)}
                          placeholder="Filter phone numbers (e.g., 123)"
                          className="w-64 text-sm"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPhoneFilter('');
                            setPhoneList(allPhones);
                          }}
                        >
                          Reset
                        </Button>
                      </div>
                    )}
                  </div>
                  {loadingPhones ? (
                    <div className="flex items-center justify-center p-8 border-2 border-dashed rounded-lg">
                      <div className="text-center">
                        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Loading phone numbers...</p>
                      </div>
                    </div>
                  ) : phoneList.length === 0 ? (
                    <div className="p-8 border-2 border-dashed rounded-lg text-center">
                      <Phone className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-500 mb-4">No phone numbers in list</p>
                      {/* Add Phone Form */}
                      <div className="flex items-center justify-center gap-2 max-w-md mx-auto">
                        <Input
                          type="tel"
                          value={manualPhone}
                          onChange={(e) => setManualPhone(e.target.value)}
                          placeholder="Enter phone number"
                          className="flex-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddManualPhone();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          onClick={handleAddManualPhone}
                          disabled={!manualPhone.trim()}
                          className="gap-2"
                        >
                          <Send className="h-4 w-4" />
                          Add Phone
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Quick Filter Buttons */}
                  {!loadingPhones && allPhones.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-gray-500">Quick Filters:</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          // Filter for Indian numbers (10 digits only, no country code)
                          const indianNumbers = allPhones.filter(phone => {
                            // Remove all non-digit characters
                            const digitsOnly = phone.replace(/\D/g, '');
                            // Check if it's exactly 10 digits
                            return digitsOnly.length === 10 && !removedPhones.has(phone);
                          });
                          setPhoneList(indianNumbers);
                          setPhoneFilter('Indian (10 digits)');
                        }}
                      >
                        Indian Numbers (10 digits)
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          // Filter for numbers with +91 country code
                          const withCountryCode = allPhones.filter(phone => 
                            phone.includes('+91') && !removedPhones.has(phone)
                          );
                          setPhoneList(withCountryCode);
                          setPhoneFilter('+91');
                        }}
                      >
                        +91 Country Code
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          // Filter for all Indian numbers (10 digits OR starts with +91)
                          const allIndian = allPhones.filter(phone => {
                            const digitsOnly = phone.replace(/\D/g, '');
                            const is10Digit = digitsOnly.length === 10;
                            const hasCountryCode = phone.includes('+91');
                            return (is10Digit || hasCountryCode) && !removedPhones.has(phone);
                          });
                          setPhoneList(allIndian);
                          setPhoneFilter('All Indian');
                        }}
                      >
                        All Indian Numbers
                      </Button>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(phoneList.join(', '));
                            toast.success(`Copied ${phoneList.length} phone numbers to clipboard`);
                          } catch (error) {
                            toast.error('Failed to copy to clipboard');
                          }
                        }}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy All ({phoneList.length})
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="text-xs h-7"
                        onClick={() => {
                          setPhoneList([]);
                          setRemovedPhones(new Set(allPhones));
                        }}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Remove All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          setPhoneList(allPhones);
                          setRemovedPhones(new Set());
                          setPhoneFilter('');
                        }}
                      >
                        Select All
                      </Button>
                    </div>
                  )}
                  
                  {!loadingPhones && phoneList.length > 0 && (
                    <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
                      <div className="flex flex-wrap gap-2">
                        {phoneList.map(phone => (
                          <div 
                            key={phone} 
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border shadow-sm text-sm"
                          >
                            <Phone className="h-3.5 w-3.5 text-blue-500" />
                            <span className="font-medium">{phone}</span>
                            <button
                              type="button"
                              onClick={() => handleRemovePhone(phone)}
                              className="p-0.5 hover:bg-red-100 rounded-full transition-colors"
                            >
                              <X className="h-3.5 w-3.5 text-red-500" />
                            </button>
                          </div>
                        ))}
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border shadow-sm text-sm">
                          <Phone className="h-3.5 w-3.5 text-blue-500" />
                          <input
                            type="text"
                            value={manualPhone}
                            onChange={(e) => setManualPhone(e.target.value)}
                            placeholder="Add phone"
                            className="w-24 text-sm outline-none"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddManualPhone();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleAddManualPhone}
                            className="p-0.5 hover:bg-green-100 rounded-full transition-colors"
                          >
                            <Send className="h-3.5 w-3.5 text-green-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label className="block font-medium text-gray-700">
                    Message *
                  </label>
                  <textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="Enter SMS message..."
                    className="w-full h-64 p-3 border rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSendSmsModal(false);
                  setSmsMessage('');
                  setPhoneList([]);
                  setRemovedPhones(new Set());
                }}
                className="flex-1"
                disabled={sendingSms}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSendSms}
                disabled={sendingSms || phoneList.length === 0 || !smsMessage}
                className="flex-1 gap-2"
              >
                {sendingSms ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4" />
                    Send to {phoneList.length} recipient{phoneList.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}